import { Controller } from "@hotwired/stimulus"

const TAB_TARGET = "[data-railsui-tabs-target='tab']"
const TABLIST_SELECTOR = '[role="tablist"]'

const NAVIGATION_KEYS = new Set(["ArrowLeft", "ArrowRight", "Home", "End"])
const ACTIVATION_KEYS = new Set(["Enter", " "])
const PIN_FLIP_MS = 200
const PIN_FLIP_TIMEOUT_MS = PIN_FLIP_MS + 50

// Keep in sync with AquiferUi::TabHelper pin chrome — pinned visibility comes from
// .railsui-tab-pin--pinned in tabs.css. Do not stamp opacity-* utilities: they sit in
// @layer utilities and block group-hover/row:opacity-100 after a client-side pin.
const PIN_TEXT_ON = "text-[var(--ds-tabs-hover-content)]"
const PIN_TEXT_OFF = "text-[var(--ds-tabs-passive-line)]"
const PIN_TEXT_ACTIVE_ROW = "group-[.railsui-tab--active]/row:text-[var(--ds-tabs-active-content)]"

function splitClassTokens(value) {
  return (value || "").split(/\s+/).filter(Boolean)
}

function clampIndex(index, length) {
  if (length <= 0) return 0
  return Math.min(Math.max(index, 0), length - 1)
}

function wrapIndex(index, delta, length) {
  return (index + delta + length) % length
}

function parseDataIndex(raw, fallback = "0") {
  const n = parseInt(raw ?? fallback, 10)
  return Number.isFinite(n) ? n : 0
}

function escapeSelectorValue(value) {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value)
  }
  return String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"')
}

function swapAppearanceClasses(element, useActive, activeClasses, inactiveClasses) {
  if (useActive) {
    element.classList.remove(...inactiveClasses)
    element.classList.add(...activeClasses)
  } else {
    element.classList.remove(...activeClasses)
    element.classList.add(...inactiveClasses)
  }
}

export default class extends Controller {
  static targets = ["tablist", "tab", "panel"]

  connect() {
    this.activeTabClasses = splitClassTokens(this.data.get("activeTab") || "active")
    this.inactiveTabClasses = splitClassTokens(this.data.get("inactiveTab") || "inactive")

    this.tablistElement = this.resolveTablistElement()
    this.keyboardRootElement = this.reorderRootElement() || this.tablistElement
    this.captureDefaultOrdering()
    this.syncTablistOwns()
    this.boundTablistKeydown = this.onTablistKeydown.bind(this)
    this.keyboardRootElement?.addEventListener("keydown", this.boundTablistKeydown, true)

    const selected = this.resolveInitialSelectedIndex()
    this.data.set("index", String(selected))
    this.focusedIndex = selected
    this.applyTabState()
    this.restoreFocusIfNeeded()
  }

  restoreFocusIfNeeded() {
    try {
      if (sessionStorage.getItem("railsui-tabs-restore-focus") !== "1") return
      sessionStorage.removeItem("railsui-tabs-restore-focus")
    } catch (_e) {
      return
    }

    const tab = this.tabTargets[this.index]
    if (tab) {
      requestAnimationFrame(() => tab.focus({ preventScroll: true }))
    }
  }

  disconnect() {
    this.clearFlip()
    if (this.keyboardRootElement && this.boundTablistKeydown) {
      this.keyboardRootElement.removeEventListener("keydown", this.boundTablistKeydown, true)
    }
  }

  change(event) {
    if (event.target instanceof Element && event.target.closest("form")) {
      return
    }

    const idx = this.resolveTabIndexFromEvent(event)
    if (idx < 0) return

    event.preventDefault()
    event.stopPropagation()
    this.navigateOrActivate(idx)
  }

  async pin(event) {
    event.stopPropagation()
    event.preventDefault()

    const button = event.currentTarget
    if (!(button instanceof Element)) return
    if (this.pinningInFlight) return

    const tab = this.resolveTabFromPinButton(button)
    if (!tab) return

    const currentlyPinned = button.dataset.pinPinned === "true"
    const nextPinned = !currentlyPinned
    const tabKey = button.dataset.tabKey || tab.dataset.tabKey || tab.id
    const tabLabel = button.dataset.pinLabel || tab.textContent?.trim() || "tab"
    const demoOnly = button.dataset.pinDemoOnly === "true"
    const form = button.closest("form")

    if (demoOnly) {
      this.applyPinReorder(button, tab, nextPinned, tabLabel)
      this.activateTabByKey(tabKey)
      return
    }

    if (!form) return

    this.pinningInFlight = true
    const snapshot = this.snapshotPinLayout()
    this.applyPinReorder(button, tab, nextPinned, tabLabel)

    try {
      const headers = { Accept: "text/html, application/xhtml+xml" }
      const token = document.querySelector("meta[name='csrf-token']")?.getAttribute("content")
      if (token) headers["X-CSRF-Token"] = token

      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        credentials: "same-origin",
        headers,
        redirect: "manual",
      })

      const persisted =
        response.ok ||
        response.type === "opaqueredirect" ||
        (response.status >= 300 && response.status < 400)

      if (!persisted) {
        this.restorePinLayout(snapshot)
        return
      }

      if (typeof Turbo !== "undefined" && Turbo.cache?.clear) {
        Turbo.cache.clear()
      }
    } catch (_error) {
      this.restorePinLayout(snapshot)
    } finally {
      this.pinningInFlight = false
    }
  }

  onTablistKeydown(event) {
    if (!(event.target instanceof Element)) return
    if (event.ctrlKey || event.metaKey || event.altKey) return

    const { key } = event
    if (!NAVIGATION_KEYS.has(key) && !ACTIVATION_KEYS.has(key)) return

    // Pin is a separate control: Enter/Space must pin/unpin, not activate the tab.
    if (this.isPinControl(event.target) && ACTIVATION_KEYS.has(key)) return

    const tab = this.resolveTabFromElement(event.target)
    if (!tab) return

    const currentIndex = this.tabTargets.indexOf(tab)
    if (currentIndex < 0) return

    if (ACTIVATION_KEYS.has(key)) {
      event.preventDefault()
      this.navigateOrActivate(currentIndex, { restoreFocus: true })
      return
    }

    event.preventDefault()

    const length = this.tabTargets.length
    if (length === 0) return

    const next = this.nextIndexForNavigationKey(currentIndex, key, length)
    if (next === null) return

    this.moveFocusToIndex(next)
  }

  navigateOrActivate(idx, { restoreFocus = false } = {}) {
    const tab = this.tabTargets[idx]
    const url = tab?.dataset?.url
    if (url) {
      this.visitUrl(url, { restoreFocus })
      return
    }

    this.activateTabAt(idx)
  }

  visitUrl(url, { restoreFocus = false } = {}) {
    if (restoreFocus) {
      try {
        sessionStorage.setItem("railsui-tabs-restore-focus", "1")
      } catch (_e) {}
    }
    if (typeof Turbo !== "undefined" && Turbo.visit) {
      Turbo.visit(url)
    } else {
      window.location.assign(url)
    }
  }

  resolveTablistElement() {
    if (this.hasTablistTarget) return this.tablistTarget
    return this.element.querySelector(TABLIST_SELECTOR)
  }

  isPinControl(element) {
    return Boolean(element.closest(".railsui-tab-pin, .railsui-tab-pin-form"))
  }

  resolveTabFromPinButton(button) {
    const key = button?.dataset?.tabKey
    if (key) {
      const byKey = this.tabTargets.find((t) => this.tabKeyForElement(t) === key)
      if (byKey) return byKey
    }

    const row = button?.closest("[data-railsui-tabs-tab-row]")
    const rowTab = row?.querySelector(TAB_TARGET)
    if (rowTab && this.tabTargets.includes(rowTab)) return rowTab

    const idx = parseDataIndex(button?.dataset?.index)
    return this.tabTargets[idx] || null
  }

  resolveTabFromElement(element) {
    const tab = element.closest(TAB_TARGET)
    if (tab && this.tabTargets.includes(tab)) return tab

    // Pin is a flex sibling of the tab inside the row — arrows still move across tabs.
    if (!this.isPinControl(element)) return null

    const pin = element.closest(".railsui-tab-pin") || element
    return this.resolveTabFromPinButton(pin)
  }

  appearanceTargetFor(tab) {
    return tab.closest("[data-railsui-tabs-tab-row]") || tab
  }

  resolveTabIndexFromEvent(event) {
    const fromTarget =
      event.target instanceof Element ? event.target.closest(TAB_TARGET) : null
    const fromRow =
      event.target instanceof Element
        ? event.target.closest("[data-railsui-tabs-tab-row]")?.querySelector(TAB_TARGET)
        : null
    const current = fromTarget || fromRow || event.currentTarget

    if (!(current instanceof Element)) return -1

    if (current.dataset.index !== undefined && current.dataset.index !== "") {
      return parseInt(current.dataset.index, 10)
    }
    if (current.dataset.id) {
      return this.tabTargets.findIndex((tab) => tab.id === current.dataset.id)
    }
    return this.tabTargets.indexOf(current)
  }

  resolveInitialSelectedIndex() {
    // URL is source of truth for active tab when tabs carry data-url (?tab=).
    const fromUrl = this.resolveIndexFromLocation()
    if (fromUrl >= 0) return fromUrl

    let selected = this.tabTargets.findIndex((t) => t.getAttribute("aria-selected") === "true")
    if (selected < 0) selected = 0

    if (this.anchor) {
      const fromHash = this.tabTargets.findIndex((tab) => tab.id === this.anchor)
      if (fromHash >= 0) selected = fromHash
    }

    return clampIndex(selected, this.tabTargets.length)
  }

  resolveIndexFromLocation() {
    const hrefTabs = this.tabTargets.filter((tab) => tab.dataset.url)
    if (hrefTabs.length === 0) return -1

    const current = new URL(window.location.href)
    const tabParam = current.searchParams.get("tab")

    if (tabParam) {
      const byKey = this.tabTargets.findIndex((tab) => this.tabKeyForElement(tab) === tabParam)
      if (byKey >= 0) return byKey
    }

    const byHref = this.tabTargets.findIndex((tab) => {
      if (!tab.dataset.url) return false
      try {
        const tabUrl = new URL(tab.dataset.url, window.location.origin)
        return (
          tabUrl.pathname === current.pathname &&
          tabUrl.searchParams.get("tab") === current.searchParams.get("tab")
        )
      } catch {
        return false
      }
    })
    return byHref
  }

  tablistIsRtl() {
    if (!this.tablistElement) return false
    if (this.tablistElement.getAttribute("dir") === "rtl") return true
    return getComputedStyle(this.tablistElement).direction === "rtl"
  }

  nextIndexForNavigationKey(currentIndex, key, length) {
    const rtl = this.tablistIsRtl()
    const next = rtl ? -1 : 1
    const prev = rtl ? 1 : -1

    switch (key) {
      case "ArrowRight":
        return wrapIndex(currentIndex, next, length)
      case "ArrowLeft":
        return wrapIndex(currentIndex, prev, length)
      case "Home":
        return 0
      case "End":
        return length - 1
      default:
        return null
    }
  }

  moveFocusToIndex(index) {
    const length = this.tabTargets.length
    if (length === 0) return

    const bounded = clampIndex(index, length)
    this.focusedIndex = bounded
    this.applyTabState()
    this.focusTabAt(bounded)
  }

  activateTabAt(index) {
    this.index = index
    this.focusTabAt(this.focusedIndex)
  }

  focusTabAt(index) {
    requestAnimationFrame(() => {
      this.tabTargets[index]?.focus({ preventScroll: true })
    })
  }

  applyTabState() {
    const selected = this.index

    this.tabTargets.forEach((tab, i) => {
      const panel = this.hasPanelTarget ? this.panelTargets[i] : null
      const isSelected = i === selected

      if (panel) {
        if (isSelected) {
          panel.removeAttribute("hidden")
          panel.tabIndex = 0
        } else {
          panel.setAttribute("hidden", "")
          panel.tabIndex = -1
        }
      }

      swapAppearanceClasses(
        this.appearanceTargetFor(tab),
        isSelected,
        this.activeTabClasses,
        this.inactiveTabClasses
      )
      tab.setAttribute("aria-selected", String(isSelected))
      // All tabs stay in the Tab order so keyboard users can Tab through
      // tab → pin → tab → pin. Arrow keys still move focus between tabs.
      tab.tabIndex = 0
    })

    window.dispatchEvent(new CustomEvent("tsc:tab-change"))
  }

  clearPinnedState() {
    this.element.querySelectorAll(".railsui-tab-pin").forEach((pinButton) => {
      const label = pinButton.dataset.pinLabel || "tab"
      this.syncPinButtonVisual(pinButton, false, label)
    })

    this.tabTargets.forEach((tab) => {
      tab.dataset.pinned = "false"
      this.appearanceTargetFor(tab).classList.remove("railsui-tab--pinned")
    })
  }

  applyPinReorder(button, tab, nextPinned, tabLabel) {
    if (nextPinned) this.clearPinnedState()
    this.applyPinnedState(button, tab, nextPinned, tabLabel)
    this.reorderRowsAndPanels({ animate: true })
  }

  snapshotPinLayout() {
    return this.tabRows().map((row) => {
      const pinButton = row.querySelector(".railsui-tab-pin")
      const tab = row.querySelector(TAB_TARGET)
      return {
        row,
        pinned: this.rowIsPinned(row),
        pinLabel: pinButton?.dataset?.pinLabel || tab?.textContent?.trim() || "tab",
      }
    })
  }

  restorePinLayout(snapshot) {
    const root = this.reorderRootElement()
    if (!root || !snapshot?.length) return

    this.clearFlip()
    this.clearPinnedState()
    snapshot.forEach((entry) => {
      const tab = entry.row.querySelector(TAB_TARGET)
      const pinButton = entry.row.querySelector(".railsui-tab-pin")
      if (tab && pinButton) {
        this.applyPinnedState(pinButton, tab, entry.pinned, entry.pinLabel)
      }
      root.appendChild(entry.row)
    })
    this.reorderPanels(snapshot.map((entry) => entry.row))
    this.reindexTabs(this.tabRows())
    this.syncTablistOwns()
  }

  prefersReducedMotion() {
    return Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches)
  }

  pinAnimationEnabled() {
    return this.data.get("animatePin") === "true"
  }

  captureRowLefts(rows) {
    return new Map(rows.map((row) => [row, row.getBoundingClientRect().left]))
  }

  clearFlip() {
    if (this.flipRafId != null) {
      cancelAnimationFrame(this.flipRafId)
      this.flipRafId = null
    }
    if (this.flipTimeoutId != null) {
      clearTimeout(this.flipTimeoutId)
      this.flipTimeoutId = null
    }

    const rows = this.flipRows || []
    const done = this.flipDone
    this.flipRows = []
    this.flipDone = null

    rows.forEach((row) => {
      if (done) row.removeEventListener("transitionend", done)
      row.style.transform = ""
      row.style.transition = ""
    })
  }

  playFlip(rows, firstLefts) {
    this.clearFlip()

    const moving = []
    rows.forEach((row) => {
      const dx = firstLefts.get(row) - row.getBoundingClientRect().left
      if (!Number.isFinite(dx) || Math.abs(dx) < 1) return
      row.style.transform = `translateX(${dx}px)`
      row.style.transition = "none"
      moving.push(row)
    })
    if (moving.length === 0) return

    void moving[0].offsetWidth

    this.flipRows = moving
    this.flipDone = (event) => {
      if (event && event.propertyName && event.propertyName !== "transform") return
      const row = event.currentTarget
      row.style.transform = ""
      row.style.transition = ""
      row.removeEventListener("transitionend", this.flipDone)
    }

    this.flipRafId = requestAnimationFrame(() => {
      this.flipRafId = null
      moving.forEach((row) => {
        row.style.transition = `transform ${PIN_FLIP_MS}ms ease`
        row.style.transform = "translateX(0)"
        row.addEventListener("transitionend", this.flipDone)
      })
      this.flipTimeoutId = setTimeout(() => this.clearFlip(), PIN_FLIP_TIMEOUT_MS)
    })
  }

  syncPinButtonVisual(button, pinned, tabLabel) {
    button.dataset.pinPinned = String(pinned)
    button.classList.toggle("railsui-tab-pin--pinned", pinned)
    // Drop legacy opacity utilities (fixture/server leftovers) so CSS hover reveal works.
    button.classList.remove("opacity-100", "opacity-0")
    button.classList.toggle(PIN_TEXT_ON, pinned)
    button.classList.toggle(PIN_TEXT_OFF, !pinned)
    button.classList.toggle(PIN_TEXT_ACTIVE_ROW, pinned)
    button.setAttribute("aria-label", `${pinned ? "Unpin" : "Pin"} ${tabLabel}`)
  }

  applyPinnedState(button, tab, pinned, tabLabel) {
    this.syncPinButtonVisual(button, pinned, tabLabel)
    tab.dataset.pinned = String(pinned)
    this.appearanceTargetFor(tab).classList.toggle("railsui-tab--pinned", pinned)
  }

  captureDefaultOrdering() {
    this.tabRows().forEach((row, index) => {
      if (!row.dataset.defaultIndex) row.dataset.defaultIndex = String(index)
    })
  }

  tabRows() {
    return Array.from(this.element.querySelectorAll("[data-railsui-tabs-tab-row]"))
  }

  rowDefaultIndex(row) {
    return parseDataIndex(row?.dataset?.defaultIndex, "0")
  }

  rowIsPinned(row) {
    const tab = row.querySelector(TAB_TARGET)
    return tab?.dataset?.pinned === "true"
  }

  tabKeyForElement(element) {
    return element?.dataset?.tabKey || element?.id || ""
  }

  reorderRootElement() {
    return (
      this.element.querySelector("[data-railsui-tabs-tab-strip]") ||
      this.tablistElement?.parentElement ||
      this.element.querySelector(".railsui-tabs-strip") ||
      null
    )
  }

  syncTablistOwns() {
    if (!this.tablistElement) return

    const ids = this.tabTargets.map((tab) => tab.id).filter(Boolean)
    if (ids.length > 0) {
      this.tablistElement.setAttribute("aria-owns", ids.join(" "))
    } else {
      this.tablistElement.removeAttribute("aria-owns")
    }
  }

  reorderRowsAndPanels({ animate = false } = {}) {
    const root = this.reorderRootElement()
    if (!root) return

    const rows = this.tabRows()
    if (rows.length === 0) return

    const shouldAnimate =
      animate && this.pinAnimationEnabled() && !this.prefersReducedMotion()
    const firstLefts = shouldAnimate ? this.captureRowLefts(rows) : null

    const sortedRows = rows.sort((left, right) => {
      const leftPinned = this.rowIsPinned(left) ? 0 : 1
      const rightPinned = this.rowIsPinned(right) ? 0 : 1
      if (leftPinned !== rightPinned) return leftPinned - rightPinned
      return this.rowDefaultIndex(left) - this.rowDefaultIndex(right)
    })

    sortedRows.forEach((row) => root.appendChild(row))
    this.reorderPanels(sortedRows)
    this.reindexTabs(sortedRows)
    this.syncTablistOwns()

    if (firstLefts) this.playFlip(sortedRows, firstLefts)
  }

  reorderPanels(sortedRows) {
    sortedRows.forEach((row) => {
      const tab = row.querySelector(TAB_TARGET)
      const panelId = tab?.getAttribute("aria-controls")
      if (!panelId) return

      const panel = document.getElementById(panelId)
      if (!panel) return

      this.element.appendChild(panel)
    })
  }

  reindexTabs(sortedRows) {
    sortedRows.forEach((row, index) => {
      row.dataset.index = String(index)

      const tab = row.querySelector(TAB_TARGET)
      if (tab) tab.dataset.index = String(index)

      const key = row.dataset.tabKey || this.tabKeyForElement(tab)
      if (!key) return

      const pinButton = this.element.querySelector(
        `.railsui-tab-pin[data-tab-key="${escapeSelectorValue(key)}"]`
      )
      if (pinButton) pinButton.dataset.index = String(index)
    })
    this.syncSelectedIndexAfterReorder()
  }

  syncSelectedIndexAfterReorder() {
    const selected = this.tabTargets.findIndex((tab) => tab.getAttribute("aria-selected") === "true")
    if (selected < 0) return
    this.data.set("index", String(selected))
  }

  activateTabByKey(tabKey) {
    const nextIndex = this.tabTargets.findIndex((tab) => this.tabKeyForElement(tab) === tabKey)
    this.activateTabAt(nextIndex >= 0 ? nextIndex : 0)
  }

  get index() {
    return parseDataIndex(this.data.get("index"))
  }

  set index(value) {
    const length = this.tabTargets.length
    const bounded = clampIndex(Number.isFinite(value) ? value : 0, length)

    this.data.set("index", String(bounded))
    this.focusedIndex = bounded
    this.applyTabState()
  }

  get anchor() {
    const hash = document.URL.split("#")[1]
    return hash || null
  }
}
